import React, { useEffect, useState } from 'react'

import TabContext from '@mui/lab/TabContext'
import TabList from '@mui/lab/TabList'
import TabPanel from '@mui/lab/TabPanel'
import { Box, Tab } from '@mui/material'
import useAuth from '../../../../hooks/useAuth'
import { CompanyIssues } from './CompanyIssues'
import { CompanyWarrants } from './CompanyWarrants'
import IssueApi from '../../../../api/IssueApi'
import WarrantApi from '../../../../api/WarrantApi'
import { IssueEntity } from 'types/IssueEntity'
import { IsinEntity } from '@finance-lab/utils/types'
import { WarrantEntity } from 'types/WarrantEntity'
import { hasSourceOfType } from '../../../../components/utils/calculateIssueStatus'

const sortAndSetIssuesFunction = <T extends Pick<IssueEntity, 'statusComplete' | 'subEndDate' | 'publicationDate'>>(
    issues: Array<T>,
) => {
    if (!issues) return []

    const sortedIssues = issues.sort((a, b) => {
        // First sort by statusComplete (0 before 1)
        if (a.statusComplete !== b.statusComplete) {
            return (a.statusComplete ?? 0) - (b.statusComplete ?? 0)
        }

        // If statusComplete values are the same, sort by date
        const s1 = new Date(a.subEndDate ? a.subEndDate : (a.publicationDate ?? 0))
        const s2 = new Date(b.subEndDate ? b.subEndDate : (b.publicationDate ?? 0))

        return s2.getTime() - s1.getTime() // Newest dates first
    })

    return sortedIssues
}

const issueApi = new IssueApi()
const warrantApi = new WarrantApi()

export const CompanyEvents = ({
    selectedIsin,
    setSelectedIsin,
    onArchiveIsin,
}: {
    onArchiveIsin: (isin: IsinEntity, archive?: boolean) => Promise<void>
    selectedIsin: IsinEntity | null
    setSelectedIsin: (v: IsinEntity | null) => void
}) => {
    const [issues, setIssues] = useState<IssueEntity[] | null>(null)
    const [warrants, setWarrants] = useState<WarrantEntity[] | null>(null)
    const [selectedTab, setSelectedTab] = useState<'issues' | 'warrants'>('issues')
    const { isAuthenticated } = useAuth()

    useEffect(() => {
        if (!selectedIsin) return

        if (selectedIsin.instrumentType === 'warrant') {
            setSelectedTab('warrants')
        } else {
            setSelectedTab('issues')
        }
    }, [selectedIsin])

    useEffect(() => {
        const fetchData = async () => {
            if (!isAuthenticated || !selectedIsin?.companyId) return

            const result = await issueApi.findByCompanyId(selectedIsin.companyId, false)

            const filteredIssues = result.filter(
                (issue) =>
                    issue.type === 'Rights issue' &&
                    (selectedIsin.instrumentType === 'unit' ? issue.isinUnits === null : issue.isinRights === null) &&
                    !hasSourceOfType(issue, 'Cancellation'),
            )
            setIssues(sortAndSetIssuesFunction(filteredIssues))
        }
        void fetchData()
    }, [isAuthenticated, selectedIsin, setIssues])

    useEffect(() => {
        const fetchData = async () => {
            if (!isAuthenticated || !selectedIsin?.companyId) return

            const result = await warrantApi.findByCompanyId(selectedIsin.companyId)
            // keep only one of each warrant (by id)
            const uniqueWarrants = result.filter(
                (w, i) => !w.unlisted && w.isinWarrant === null && result.findIndex((w2) => w2.id === w.id) === i,
            )

            uniqueWarrants.sort((a, b) => {
                // Get the newest subEndDate for warrant `a`
                const s1 =
                    a.subscriptionPeriods && a.subscriptionPeriods.length
                        ? Math.max(...a.subscriptionPeriods.map((period) => new Date(period.subEndDate ?? 0).getTime()))
                        : null

                // Get the newest subEndDate for warrant `b`
                const s2 =
                    b.subscriptionPeriods && b.subscriptionPeriods.length
                        ? Math.max(...b.subscriptionPeriods.map((period) => new Date(period.subEndDate ?? 0).getTime()))
                        : null

                // Sort by newest subEndDate first
                if (s1 === null && s2 === null) return 0 // Both are null, they are equal
                if (s1 === null) return 1 // `a` has no date, `b` should come first
                if (s2 === null) return -1 // `b` has no date, `a` should come first

                return s2 - s1 // Newest dates first
            })

            setWarrants(uniqueWarrants)
        }
        void fetchData()
    }, [isAuthenticated, selectedIsin, setWarrants])

    if (selectedIsin === null) return null

    return (
        <TabContext value={selectedTab}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <TabList onChange={(e, newValue) => setSelectedTab(newValue)}>
                    <Tab label='Emissioner' value='issues' disabled={selectedIsin.instrumentType === 'warrant'} />
                    <Tab
                        label='Teckningsoptioner'
                        value='warrants'
                        disabled={selectedIsin.instrumentType !== 'warrant'}
                    />
                </TabList>
            </Box>
            <TabPanel value='issues'>
                <CompanyIssues
                    issues={issues}
                    selectedIsin={selectedIsin}
                    setSelectedIsin={setSelectedIsin}
                    onArchiveIsin={onArchiveIsin}
                />
            </TabPanel>
            <TabPanel value='warrants'>
                <CompanyWarrants
                    warrants={warrants}
                    selectedIsin={selectedIsin}
                    setSelectedIsin={setSelectedIsin}
                    onArchiveIsin={onArchiveIsin}
                />
            </TabPanel>
        </TabContext>
    )
}
